import { Procedure } from '../define-procedure';
import { CameraService } from 'src/app/engine/camera.service';
import { Store } from '@ngxs/store';
import { MessageEnum } from '../models/message.enum';
import {
  RotateCameraStatic,
  PanCameraStatic,
  ResetCamera,
  SetCamera,
  ZoomCameraStatic,
  SetCameraMode,
  SetChapterCameraEnabled,
} from 'src/app/engine/state/camera/engine.camera.actions';
import { CameraModel } from 'src/app/engine/models/camera.model';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { RPCEvent } from '../define-event';
import {
  CameraInfoResponse,
  CameraEventResponse,
  CameraProcRequest,
  CameraProcResponse,
  CameraOrbitRequest,
  CameraPanRequest,
  CameraSetRequest,
  CameraZoomRequest,
  SetCameraModeRequest,
} from '../models/camera.io';
import { FlyToObject } from 'src/app/engine/state/engine.actions';

function formatCameraInfo(cameraService: CameraService): CameraInfoResponse {
  const capture = cameraService.captureCamera();
  return {
    position: capture.lookAt.eye,
    target: capture.lookAt.look,
    up: capture.lookAt.up,
    zoom: capture.zoom,
  };
}

export function getCameraEvents(cameraService: CameraService): RPCEvent<CameraEventResponse>[] {
  return [
    {
      eventName: EventsEnum.CameraUpdated,
      aliasEvents: [EventsEnum.CameraRested],
      mapFn: () => formatCameraInfo(cameraService),
    },
  ];
}

export function getCameraProcedures(
  store: Store,
  cameraService: CameraService,
): Procedure<CameraProcRequest, CameraProcResponse>[] {
  return [
    {
      procName: MessageEnum.CameraInfo,
      actionFn: () => formatCameraInfo(cameraService),
    },
    {
      procName: MessageEnum.CameraOrbit,
      actionFn: (params: CameraOrbitRequest) => {
        const x = params.pitch === undefined ? 0 : params.pitch;
        const y = params.yaw === undefined ? 0 : params.yaw;
        store.dispatch(new RotateCameraStatic(x, y));
        return true;
      },
    },
    {
      procName: MessageEnum.CameraPan,
      actionFn: (params: CameraPanRequest) => {
        const x = params.x === undefined ? 0 : params.x;
        const y = params.y === undefined ? 0 : params.y;
        const z = params.z === undefined ? 0 : params.z;
        store.dispatch(new PanCameraStatic(x, y, z));
        return true;
      },
    },
    {
      procName: MessageEnum.CameraReset,
      actionFn: () => store.dispatch(new ResetCamera()),
    },
    {
      procName: MessageEnum.CameraSet,
      actionFn: (params: CameraSetRequest) => {
        const animate = params.animate || false;

        if (params.objectId) {
          store.dispatch(new FlyToObject(params.objectId, animate, params.animationDuration));
          return true;
        }

        const currentCamera = formatCameraInfo(cameraService);
        const destination: CameraModel = {
          eye: params.position || currentCamera.position,
          look: params.target || currentCamera.target,
          up: params.up || currentCamera.up,
        };

        store.dispatch(new SetCamera(destination, animate));
        return true;
      },
    },
    {
      procName: MessageEnum.CameraZoom,
      actionFn: (zoom: CameraZoomRequest) => {
        store.dispatch(new ZoomCameraStatic(zoom || 0));
        return true;
      },
    },
    {
      procName: MessageEnum.setCameraMode,
      actionFn: (mode: SetCameraModeRequest) => {
        store.dispatch(new SetCameraMode(mode === 'pan' ? 'pan' : 'rotate'));
        return true;
      },
    },
    {
      procName: MessageEnum.CameraDisableChapterCamera,
      actionFn: () => {
        store.dispatch(new SetChapterCameraEnabled(false));
        return true;
      },
    },
    {
      procName: MessageEnum.CameraEnableChapterCamera,
      actionFn: () => {
        store.dispatch(new SetChapterCameraEnabled(true));
        return true;
      },
    },
  ];
}
